import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  dayKey, subscribeRoster, subscribeDay, parseCubby,
  checkIn as fsCheckIn, checkOut as fsCheckOut, setNoPhone as fsSetNoPhone,
  resetRecord as fsReset, masterCheckout as fsMasterCheckout, fetchHistory,
} from "../utils/phoneCheckIn";
import { ensureKioskAuth } from "../utils/kioskAuth";

/**
 * King's Academy — Phone Check-In Station
 *
 * Multi-station model
 * -------------------
 * Ten stations: 6 dorms + one per day-student grade. Each station is a screen
 * running this app in a browser, pointed at its own URL (e.g. /station/grade-9).
 * In production every station reads and writes the SAME roster and log through a
 * shared backend, so the daily record is one source of truth and an admin screen
 * can see the whole school.
 *
 * Visual feedback is LOCAL to each station: when a card is scanned at Grade 9,
 * that screen shows the big name + slot banner instantly. Other stations don't
 * need to see it, so there is no cross-station latency for the scan feedback.
 *
 * This prototype runs on one screen. The "This screen" picker simulates which
 * station a given URL would be locked to. It also reads ?station=<id> from the
 * URL if present, which is exactly how the real per-station URLs would work.
 * State is in-memory here; production loads the roster from the database and
 * persists the log to the shared backend.
 *
 * The card barcode is the student ID, so a scan matches a student directly with
 * no separate barcode field or lookup table.
 */

// --- Brand tokens ---------------------------------------------------------
const NAVY = "#233C5F";
const RED = "#E83530";
const GOLD = "#A29164";
const BEIGE = "#F5F0E6";

// After this hour (24h local time), a station left in Check-in auto-switches
// to Return / checkout, unless someone has already flipped the mode by hand.
const RETURN_CUTOFF_HOUR = 14; // 2:00 PM

// How long a student's result stays on the kiosk face before it clears back to
// the idle "ready to scan" screen. Kiosk view only; the admin banner persists.
const KIOSK_CLEAR_MS = 15000; // 15 seconds

function modeForNow() {
  return new Date().getHours() >= RETURN_CUTOFF_HOUR ? "return" : "checkin";
}

// Daily unlock for kiosk screens. PLACEHOLDERS: move these to the backend for
// production (see notes). A kiosk starts locked and is opened each morning by a
// staff PIN or by scanning the staff unlock card.
const STATION_PIN = "4726";          // staff PIN to unlock a kiosk
const UNLOCK_CODE = "KA-STAFF-UNLOCK"; // barcode value on the staff unlock card

// --- Stations -------------------------------------------------------------
const STATIONS = [
  { id: "sulafat-east", label: "Sulafat East", kind: "dorm" },
  { id: "sulafat-west", label: "Sulafat West", kind: "dorm" },
  { id: "alnilam", label: "Alnilam", kind: "dorm" },
  { id: "meissa", label: "Meissa", kind: "dorm" },
  { id: "murzim", label: "Murzim", kind: "dorm" },
  { id: "nihal", label: "Nihal", kind: "dorm" },
  { id: "atair", label: "Atair", kind: "dorm" },
  { id: "grade-9", label: "Grade 9 (Day)", kind: "day" },
  { id: "grade-10", label: "Grade 10 (Day)", kind: "day" },
  { id: "grade-11", label: "Grade 11 (Day)", kind: "day" },
  { id: "grade-12", label: "Grade 12 (Day)", kind: "day" },
];

const STATUS = {
  pending: { label: "Not in", color: "#8a8f98", bg: "#eef0f2" },
  in: { label: "Phone in", color: NAVY, bg: "#e5ebf3" },
  returned: { label: "Returned", color: "#2e7d32", bg: "#e7f2e8" },
  nophone: { label: "No phone", color: GOLD, bg: "#f3efe4" },
  missing: { label: "Missing", color: RED, bg: "#fdeceb" },
};

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function stationLabel(id) {
  return STATIONS.find((s) => s.id === id)?.label || id;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function stationFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search).get("station");
    if (p && STATIONS.some((s) => s.id === p)) return p;
  } catch (e) {}
  return null;
}

// Kiosk URLs add view=kiosk (minimal public screen). Anything else is the admin
// screen (full controls), which should sit behind its own access protection.
function viewFromUrl() {
  try {
    // The public /phones route is kiosk-only: the admin face can never render
    // there, no matter the view param. Admin is reached via /tools/phone-checkin.
    if (window.location.pathname.startsWith("/phones")) return "kiosk";
    return new URLSearchParams(window.location.search).get("view") === "kiosk" ? "kiosk" : "admin";
  } catch (e) {
    return "admin";
  }
}

export default function PhoneCheckIn() {
  const [rawRoster, setRawRoster] = useState([]);   // students from Firestore
  const [dayRecords, setDayRecords] = useState({}); // today's records from Firestore
  const [today, setToday] = useState(() => dayKey());
  const [history, setHistory] = useState([]);       // chronic-report history
  const [log, setLog] = useState([]);               // this session's actions (log table)
  const [stationId, setStationId] = useState(() => stationFromUrl() || "grade-9");
  const [mode, setMode] = useState(() => modeForNow()); // 'checkin' | 'return'
  const [modeManual, setModeManual] = useState(false); // true once a person flips it
  const [operator, setOperator] = useState("");
  const [early, setEarly] = useState(false);
  const [scan, setScan] = useState("");
  const [search, setSearch] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [lastScan, setLastScan] = useState(null); // { raw, value } for the diagnostic
  const [viewMode] = useState(() => viewFromUrl()); // 'kiosk' | 'admin'
  const [locked, setLocked] = useState(() => viewFromUrl() === "kiosk"); // kiosks start locked
  const [pin, setPin] = useState("");
  const [adminTab, setAdminTab] = useState("ops"); // 'ops' | 'reports'
  const [reportRange, setReportRange] = useState("week"); // 'week' | 'all'
  const [reportScope, setReportScope] = useState("all"); // 'all' | a station id
  const scanRef = useRef(null);
  const bufRef = useRef("");      // global scan buffer (focus-independent)
  const lastKeyRef = useRef(0);   // timing to tell a scanner burst from human typing

  // Live roster: students joined with today's Firestore records. The UI reads
  // `roster` exactly as before; only the source changed.
  const roster = useMemo(() => rawRoster.map((s) => {
    const first = s.firstName || (s.name ? String(s.name).trim().split(/\s+/)[0] : s.id);
    const name = s.name || [s.firstName, s.lastName].filter(Boolean).join(" ") || first;
    const info = parseCubby(s.cubby); // cubby is the source of truth for station + slot
    const rec = dayRecords[s.id];
    const status = rec?.status || (s.noPhone ? "nophone" : "pending");
    return { id: s.id, name, first, station: info ? info.station : null, slot: info ? info.slot : null, status };
  }), [rawRoster, dayRecords]);

  function isEditable(el) {
    if (!el) return false;
    const tag = el.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
  }

  // Manual mode flip: remember that a person chose it, so the 2 PM auto-switch
  // leaves their choice alone.
  function chooseMode(next) {
    setMode(next);
    setModeManual(true);
    if (next === "checkin") setEarly(false);
  }

  // Keep the scan field focused when no one is typing elsewhere, so the visible
  // input is ready. This is a convenience; the global capture below is what makes
  // the scanner work even when nothing is focused.
  useEffect(() => {
    function refocus() {
      if (scanRef.current && document.activeElement !== scanRef.current && !isEditable(document.activeElement)) {
        scanRef.current.focus();
      }
    }
    const t = setInterval(refocus, 800);
    window.addEventListener("click", refocus);
    document.addEventListener("visibilitychange", refocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("click", refocus);
      document.removeEventListener("visibilitychange", refocus);
    };
  }, []);

  // Always-live scanner: capture keystrokes at the window level. When focus is
  // NOT in a text field (idle kiosk), a scanner burst ending in Enter is read
  // here directly, so no one has to click into the box first. When a field IS
  // focused, this steps aside and the field behaves normally.
  useEffect(() => {
    function onKey(e) {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (isEditable(document.activeElement)) return; // let focused fields handle it
      const now = Date.now();
      if (now - lastKeyRef.current > 120) bufRef.current = ""; // slow gap = new/human
      lastKeyRef.current = now;
      if (e.key === "Enter") {
        const code = bufRef.current.trim();
        bufRef.current = "";
        if (code) {
          e.preventDefault();
          resolveScan(code);
          if (scanRef.current) scanRef.current.focus();
        }
        return;
      }
      if (e.key.length === 1) bufRef.current += e.key;
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // At 2 PM, move a station still in Check-in over to Return, unless a person
  // already flipped the mode by hand today.
  useEffect(() => {
    const t = setInterval(() => {
      if (!modeManual && mode === "checkin" && new Date().getHours() >= RETURN_CUTOFF_HOUR) {
        setMode("return");
      }
    }, 30000);
    return () => clearInterval(t);
  }, [modeManual, mode]);

  // Live roster and today's records from Firestore.
  useEffect(() => subscribeRoster(setRawRoster), []);
  useEffect(() => subscribeDay(today, setDayRecords), [today]);

  // Midnight rollover (Amman): when the date key changes, switch to the new
  // day's records and clear the screen, so a station left running overnight
  // starts fresh instead of showing yesterday's check-ins. Checked once a
  // minute. When `today` changes, subscribeDay re-subscribes to the new date.
  useEffect(() => {
    const t = setInterval(() => {
      const k = dayKey();
      if (k !== today) {
        setToday(k);
        setDayRecords({});
        setLog([]);
        setFeedback(null);
        setMode(modeForNow());
        setModeManual(false);
      }
    }, 60000);
    return () => clearInterval(t);
  }, [today]);

  // Kiosk sign-in: provision from the URL code on first load, then take the
  // authoritative station from the token claim and unlock. If there is no
  // session and no code, the lock screen stays up for a staff PIN.
  useEffect(() => {
    if (viewMode !== "kiosk") return;
    ensureKioskAuth()
      .then((user) => {
        if (!user) return;
        return user.getIdTokenResult().then((r) => {
          if (r.claims.station) setStationId(r.claims.station);
          setLocked(false);
        });
      })
      .catch((e) => console.error("Kiosk auth failed", e));
  }, []);

  // Load history for the chronic report when the Reports tab is open.
  useEffect(() => {
    if (adminTab !== "reports") return;
    const n = reportRange === "week" ? 7 : 30;
    const keys = [];
    for (let i = 1; i <= n; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      keys.push(dayKey(d));
    }
    fetchHistory(keys).then((h) => setHistory(h.reverse())).catch(() => setHistory([]));
  }, [adminTab, reportRange]);

  // Kiosk face: clear a student's result back to idle after KIOSK_CLEAR_MS.
  // Each new scan resets the timer. Admin view is unaffected.
  useEffect(() => {
    if (viewMode !== "kiosk" || !feedback) return;
    const t = setTimeout(() => setFeedback(null), KIOSK_CLEAR_MS);
    return () => clearTimeout(t);
  }, [feedback, viewMode]);

  // Students whose home is this station.
  const stationRoster = useMemo(
    () => roster.filter((s) => s.station === stationId),
    [roster, stationId]
  );

  const counts = useMemo(() => {
    const c = { pending: 0, in: 0, returned: 0, nophone: 0 };
    stationRoster.forEach((s) => (c[s.status] = (c[s.status] || 0) + 1));
    return c;
  }, [stationRoster]);

  const stationLog = useMemo(
    () => log.filter((r) => r.station === stationId),
    [log, stationId]
  );

  function addLog(entry) {
    setLog((prev) => [{ time: nowTime(), station: stationId, ...entry }, ...prev]);
  }

  function processStudent(student, note, allowAway = false) {
    const away = student.station && student.station !== stationId;
    // Hard block: a scan at the wrong station is refused. The name lookup passes
    // allowAway so staff can still handle a cross-station exception.
    if (away && !allowAway) {
      setFeedback({
        kind: "err",
        title: student.name,
        sid: student.id,
        kioskMsg: "Wrong station",
        detail: `${student.name} belongs to ${stationLabel(student.station)}, not ${stationLabel(stationId)}. Phone cannot be turned in here.`,
      });
      return;
    }
    const where = away ? ` (home: ${stationLabel(student.station)}, slot ${student.slot})` : ` slot ${student.slot}`;
    const tail = note ? ` [${note}]` : "";
    if (mode === "checkin") {
      if (student.status === "nophone") {
        setFeedback({ kind: "warn", title: student.name, sid: student.id, kioskMsg: "No phone on file", detail: "Marked as no phone. Nothing to check in." });
        return;
      }
      if (student.status === "in") {
        setFeedback({ kind: "warn", title: student.name, sid: student.id, kioskMsg: "Already checked in", detail: `Already checked in.${where}.` });
        return;
      }
      fsCheckIn(today, student.id, { station: stationId, operator });
      addLog({ id: student.id, name: student.name, action: "Check-in", operator, early: false });
      setFeedback({ kind: "ok", title: student.name, first: student.first, sid: student.id, kioskMsg: "Phone Checked In", slot: student.slot, away, awayLabel: away ? stationLabel(student.station) : null, detail: `Phone in. Place in slot ${student.slot}${away ? ` at ${stationLabel(student.station)}` : ""}.${tail}` });
    } else {
      if (student.status === "pending") {
        setFeedback({ kind: "warn", title: student.name, sid: student.id, kioskMsg: "No phone was checked in", detail: "No phone was checked in today." });
        return;
      }
      if (student.status === "nophone") {
        setFeedback({ kind: "warn", title: student.name, sid: student.id, kioskMsg: "No phone on file", detail: "No phone on file." });
        return;
      }
      if (student.status === "returned") {
        setFeedback({ kind: "warn", title: student.name, sid: student.id, kioskMsg: "Already checked out", detail: `Already returned.${where}.` });
        return;
      }
      fsCheckOut(today, student.id, { station: stationId, operator, note: null, early });
      addLog({ id: student.id, name: student.name, action: early ? "Early checkout" : "Return", operator, early });
      setFeedback({ kind: "ok", title: student.name, first: student.first, sid: student.id, kioskMsg: "Phone Checked Out", slot: student.slot, away, awayLabel: away ? stationLabel(student.station) : null, detail: `${early ? "Early checkout" : "Returned"}. Retrieve from slot ${student.slot}${away ? ` at ${stationLabel(student.station)}` : ""}.${tail}` });
    }
  }

  // Manual check-out: release the phone to the student and log it. This DOES
  // move the student to "returned" so the phone is treated as out of the box,
  // which also lets a later scan check it back in cleanly.
  function manualCheckout(student) {
    fsCheckOut(today, student.id, { station: stationId, operator, note: "manual checkout", early: false });
    addLog({ id: student.id, name: student.name, action: "Manual checkout", operator, early: false, note: "manual" });
    setFeedback({
      kind: "ok",
      title: student.name,
      first: student.first,
      sid: student.id,
      kioskMsg: "Phone Checked Out",
      slot: student.slot,
      detail: `Manual checkout. Retrieve from slot ${student.slot}. Student can scan to check the phone back in.`,
    });
  }

  // Digits only, leading zeros removed. Used only as a fallback so a leading
  // zero or stray character from the scanner doesn't block an obvious match.
  function normId(x) {
    return String(x).replace(/\D/g, "").replace(/^0+/, "");
  }

  // Shared scan resolution, called by both the visible form and the always-live
  // global capture.
  function resolveScan(rawInput) {
    const raw = String(rawInput);
    const value = raw.trim();
    setLastScan({ raw, value });
    if (!value) return;

    // While locked, the only scan that does anything is the staff unlock card.
    if (locked) {
      if (value === UNLOCK_CODE) {
        setLocked(false);
        setFeedback({ kind: "ok", title: "Station unlocked", kioskMsg: "Ready", detail: "Ready to scan." });
      } else {
        setFeedback({ kind: "err", title: "Station locked", kioskMsg: "Station locked", detail: "Staff unlock required to start this station." });
      }
      return;
    }

    // The card barcode is the student ID, so match on id directly.
    let student = roster.find((s) => s.id === value);
    let viaNormalize = false;

    // Fallback: match on the numeric ID ignoring leading zeros, but only when
    // it resolves to exactly one student, so we never guess.
    if (!student) {
      const target = normId(value);
      if (target) {
        const matches = roster.filter((s) => normId(s.id) === target);
        if (matches.length === 1) {
          student = matches[0];
          viaNormalize = true;
        }
      }
    }

    if (!student) {
      setFeedback({
        kind: "err",
        title: "Card not recognized",
        kioskMsg: "Card not recognized",
        detail: `Received "${value}" (${value.length} chars). No student with this ID in the loaded roster.`,
      });
      return;
    }
    processStudent(student, viaNormalize ? `matched after normalizing "${value}"` : null);
  }

  function handleScan(e) {
    e.preventDefault();
    const v = scan;
    setScan("");
    resolveScan(v);
  }

  function toggleNoPhone(id) {
    const cur = roster.find((s) => s.id === id);
    fsSetNoPhone(today, id, cur ? cur.status !== "nophone" : true);
  }

  function resetStudent(id) {
    fsReset(today, id);
  }

  function exportLog() {
    const header = "time,station,id,name,action,operator,early,note\n";
    const body = log
      .map((r) => [r.time, r.station, r.id, `"${r.name}"`, r.action, `"${r.operator || ""}"`, r.early ? "yes" : "", `"${r.note || ""}"`].join(","))
      .join("\n");
    download(`phone-log-${new Date().toISOString().slice(0, 10)}.csv`, header + body);
  }

  function exportStatus() {
    const header = "id,name,station,slot,status\n";
    const body = roster
      .map((s) => [s.id, `"${s.name}"`, s.station, s.slot, STATUS[s.status].label].join(","))
      .join("\n");
    download(`phone-status-${new Date().toISOString().slice(0, 10)}.csv`, header + body);
  }

  function resetDay() {
    // Clears this screen's session log and mode. The real daily reset is the
    // 4 AM seed, which starts each day's records fresh in Firestore.
    setLog([]);
    setFeedback(null);
    setMode(modeForNow());
    setModeManual(false);
  }

  // Master checkout: release phones back to students across the whole school.
  // Records a per-student checkout event, noted as system-wide, only for
  // students whose phone was still checked in. Students already returned or
  // with no phone are left alone.
  function allCheckout() {
    const inList = roster.filter((s) => s.status === "in");
    if (!window.confirm(`Check out ALL phones to students? ${inList.length} phone(s) still checked in will be recorded as a system-wide checkout.`)) return;
    const t = nowTime();
    const recordMap = Object.fromEntries(roster.map((s) => [s.id, { status: s.status }]));
    fsMasterCheckout(today, recordMap, { operator });
    setLog((prev) => {
      const events = inList.map((s) => ({
        time: t, station: s.station, id: s.id, name: s.name,
        action: "Checkout", operator: operator || "system", early: false,
        note: "system-wide master checkout",
      }));
      return [...events.reverse(), ...prev];
    });
    setFeedback({ kind: "ok", title: "Master checkout complete", detail: `${inList.length} phone(s) checked out and recorded per student.` });
  }

  function tryPin() {
    if (pin === STATION_PIN) {
      setLocked(false);
      setPin("");
      setFeedback({ kind: "ok", title: "Station unlocked", kioskMsg: "Ready", detail: "Ready to scan." });
    } else {
      setFeedback({ kind: "err", title: "Wrong PIN", kioskMsg: "Wrong PIN", detail: "Try again or scan the staff unlock card." });
      setPin("");
    }
  }

  function lockStation() {
    setLocked(true);
    setFeedback(null);
    setLastScan(null);
  }

  // Name lookup searches ALL stations, so any screen can handle an exception
  // (for example a boarder needing an early checkout during the day).
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return roster
      .filter((s) => s.name.toLowerCase().includes(q) || s.id.includes(q))
      .slice(0, 8);
  }, [roster, search]);

  // Roster indexed by id, for joining history to names/stations.
  const byId = useMemo(() => Object.fromEntries(roster.map((s) => [s.id, s])), [roster]);

  // Real-time snapshot from the live roster: who has not checked a phone in yet.
  // This is the 8:30 AM leadership view. Uses current in-session statuses.
  const snapshot = useMemo(() => {
    const inScope = (s) => reportScope === "all" || s.station === reportScope;
    const scoped = roster.filter(inScope);
    const notIn = scoped.filter((s) => s.status === "pending"); // has a phone, not yet in
    const stillOut = scoped.filter((s) => s.status === "in");   // phone in box, not retrieved
    return {
      total: scoped.length,
      notIn,
      inCount: scoped.filter((s) => s.status === "in").length,
      returnedCount: scoped.filter((s) => s.status === "returned").length,
      noPhoneCount: scoped.filter((s) => s.status === "nophone").length,
      stillOut,
    };
  }, [roster, reportScope]);

  // Chronic-pattern report over the selected historical range. Counts, per
  // student, days they missed check-in and days they left a phone unretrieved.
  const chronic = useMemo(() => {
    const days = reportRange === "week" ? history.slice(-5) : history;
    const rows = {};
    days.forEach((d) => {
      d.entries.forEach((e) => {
        const s = byId[e.id];
        if (!s) return;
        if (reportScope !== "all" && s.station !== reportScope) return;
        if (!rows[e.id]) rows[e.id] = { id: e.id, name: s.name, station: s.station, missed: 0, left: 0 };
        if (!e.checkedIn) rows[e.id].missed += 1;
        if (e.checkedIn && !e.retrieved) rows[e.id].left += 1;
      });
    });
    return {
      dayCount: days.length,
      first: days[0]?.date,
      last: days[days.length - 1]?.date,
      list: Object.values(rows)
        .map((r) => ({ ...r, total: r.missed + r.left }))
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    };
  }, [reportRange, reportScope, byId, history]);

  function exportSnapshot() {
    const header = "id,name,station,slot,status\n";
    const body = snapshot.notIn
      .map((s) => [s.id, `"${s.name}"`, stationLabel(s.station), s.slot, "Not checked in"].join(","))
      .join("\n");
    download(`not-checked-in-${new Date().toISOString().slice(0, 10)}.csv`, header + body);
  }

  function exportChronic() {
    const header = "id,name,station,missed_checkins,left_unretrieved,total\n";
    const body = chronic.list
      .map((r) => [r.id, `"${r.name}"`, stationLabel(r.station), r.missed, r.left, r.total].join(","))
      .join("\n");
    download(`chronic-report-${new Date().toISOString().slice(0, 10)}.csv`, header + body);
  }

  const fb = feedback;
  const fbColor =
    fb?.kind === "ok" ? "#2e7d32" : fb?.kind === "warn" ? GOLD : fb?.kind === "err" ? RED : NAVY;

  // Shared lock overlay (PIN entry + scan-to-unlock prompt).
  const lockOverlay = locked ? (
    <div className="pci-lock">
      <div className="pci-lock-card">
        <div className="pci-lock-badge">Locked</div>
        <h2>{stationLabel(stationId)}</h2>
        <p>Scan the staff unlock card, or enter the staff PIN to start this station.</p>
        <div className="pci-lock-pin">
          <input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") tryPin(); }}
            placeholder="Staff PIN"
          />
          <button onClick={tryPin}>Unlock</button>
        </div>
      </div>
    </div>
  ) : null;

  // --- Kiosk view: minimal public screen ---------------------------------
  if (viewMode === "kiosk") {
    const tone = fb?.kind === "ok" ? "#2e7d32" : fb?.kind === "warn" ? GOLD : fb?.kind === "err" ? RED : NAVY;
    return (
      <div className="pci-kiosk">
        <style>{css}</style>
        {lockOverlay}
        <div className="pci-kiosk-top">
          <span>{stationLabel(stationId)}</span>
          <button className="pci-lock-btn" onClick={lockStation} title="Lock station">Lock</button>
        </div>
        {!locked && (
          <div className="pci-kiosk-body">
            <form className="pci-kiosk-scan" onSubmit={handleScan}>
              <input
                ref={scanRef}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                placeholder="Scan student card"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </form>
            {fb ? (
              <div className="pci-kiosk-card" style={{ borderColor: tone }}>
                {fb.first && <div className="pci-kiosk-name">{fb.first}</div>}
                {fb.slot != null && <div className="pci-kiosk-slot">Slot {fb.slot}</div>}
                <div className="pci-kiosk-msg" style={{ color: tone }}>{fb.kioskMsg || fb.first}</div>
              </div>
            ) : (
              <div className="pci-kiosk-idle">
                <div className="pci-kiosk-ready">Ready</div>
                <p>Scan your student card</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Admin view --------------------------------------------------------
  return (
    <div className="pci-root">
      <style>{css}</style>
      {lockOverlay}

      <header className="pci-head">
        <div>
          <h1>Phone Check-In</h1>
          <p>King's Academy</p>
        </div>
        <div className="pci-station">
          <label className="pci-screen">
            This screen
            <select value={stationId} onChange={(e) => setStationId(e.target.value)}>
              {STATIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
          <label>
            Operator
            <input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="staff name" />
          </label>
          <span className="pci-date">{new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}</span>
        </div>
      </header>

      <div className="pci-tabs">
        <button className={adminTab === "ops" ? "on" : ""} onClick={() => setAdminTab("ops")}>Station</button>
        <button className={adminTab === "reports" ? "on" : ""} onClick={() => setAdminTab("reports")}>Reports</button>
      </div>

      {adminTab === "ops" && (
      <>
      <div className="pci-mode">
        <button className={mode === "checkin" ? "on" : ""} onClick={() => chooseMode("checkin")}>Check-in</button>
        <button className={mode === "return" ? "on" : ""} onClick={() => chooseMode("return")}>Return / checkout</button>
        {mode === "return" && (
          <label className="pci-early">
            <input type="checkbox" checked={early} onChange={(e) => setEarly(e.target.checked)} />
            Early checkout
          </label>
        )}
        {mode === "checkin" && !modeManual && (
          <span className="pci-autonote">Auto-switches to checkout at 2:00 PM</span>
        )}
        {modeManual && (
          <span className="pci-autonote">Mode set manually</span>
        )}
      </div>

      <form className="pci-scan" onSubmit={handleScan}>
        <input ref={scanRef} value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan student card…" autoFocus />
        <button type="submit">Enter</button>
      </form>

      {lastScan && (() => {
        const { raw, value } = lastScan;
        const hadWhitespace = raw !== value;
        const hidden = [...raw].filter((c) => {
          const n = c.charCodeAt(0);
          return n < 32 || n === 127 || n === 0x200b || n === 0xfeff;
        });
        const inRoster = roster.some((s) => s.id === value);
        const normHit = roster.some((s) => normId(s.id) === normId(value));
        return (
          <div className="pci-diag">
            <strong>Scanner diagnostic</strong>
            <div>Received: <code>{value || "(empty)"}</code> · {value.length} characters</div>
            {hadWhitespace && <div className="pci-diag-warn">Scanner added surrounding whitespace (raw length {raw.length}). Trimmed for matching.</div>}
            {hidden.length > 0 && <div className="pci-diag-warn">Hidden characters detected: {hidden.map((c) => "0x" + c.charCodeAt(0).toString(16)).join(", ")}. Check the scanner's prefix/suffix settings.</div>}
            <div>Exact match in roster: {inRoster ? "yes" : "no"} · Numeric match (ignoring leading zeros): {normHit ? "yes" : "no"}</div>
          </div>
        );
      })()}

      {fb && (
        <div className="pci-feedback" style={{ borderColor: fbColor, background: fb.kind === "ok" ? "#f0f7f1" : "#fff" }}>
          {fb.slot != null && <div className="pci-slot" style={{ background: fb.away ? GOLD : NAVY }}>{fb.slot}</div>}
          <div>
            <strong style={{ color: fbColor }}>{fb.title}</strong>
            <div>{fb.detail}</div>
            {fb.away && <div className="pci-awaytag">Belongs to {fb.awayLabel}</div>}
          </div>
        </div>
      )}

      <div className="pci-summary">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="pci-chip" style={{ background: v.bg, color: v.color }}>
            <span className="pci-num">{counts[k] || 0}</span> {v.label}
          </div>
        ))}
        <div className="pci-chip pci-total">{stationRoster.length} at this station</div>
      </div>

      <div className="pci-tools">
        <button className="pci-allout" onClick={allCheckout}>Check out ALL phones</button>
        <button onClick={exportLog}>Export log</button>
        <button onClick={exportStatus}>Export status</button>
        <button className="pci-reset" onClick={resetDay}>Reset day</button>
        <button onClick={lockStation}>Lock station</button>
      </div>

      <div className="pci-lookup">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name lookup — searches all stations (unreadable card / early checkout)…" />
        {searchResults.length > 0 && (
          <div className="pci-results">
            {searchResults.map((s) => (
              <div key={s.id} className="pci-result">
                <div>
                  <strong>{s.name}</strong>
                  <span className="pci-result-meta">{stationLabel(s.station)} · slot {s.slot} · {STATUS[s.status].label}</span>
                </div>
                <div className="pci-result-actions">
                  <button onClick={() => { processStudent(s, undefined, true); setSearch(""); }}>
                    {mode === "checkin" ? "Check in" : early ? "Early checkout" : "Return"}
                  </button>
                  <button className="ghost" onClick={() => { manualCheckout(s); setSearch(""); }}>Manual out</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="pci-h2">{stationLabel(stationId)} — students</h2>
      <div className="pci-grid">
        {stationRoster.map((s) => (
          <div key={s.id} className="pci-card" style={{ borderLeftColor: STATUS[s.status].color }}>
            <div className="pci-card-top">
              <span className="pci-slotnum">Slot {s.slot}</span>
              <span className="pci-badge" style={{ background: STATUS[s.status].bg, color: STATUS[s.status].color }}>{STATUS[s.status].label}</span>
            </div>
            <div className="pci-name">{s.name}</div>
            <div className="pci-meta">{s.id}</div>
            <div className="pci-card-actions">
              <button onClick={() => processStudent(s)}>{mode === "checkin" ? "Check in" : early ? "Early checkout" : "Return"}</button>
              <button className="ghost" onClick={() => manualCheckout(s)}>Manual out</button>
              <button className="ghost" onClick={() => toggleNoPhone(s.id)}>{s.status === "nophone" ? "Has phone" : "No phone"}</button>
              {(s.status === "in" || s.status === "returned") && <button className="ghost" onClick={() => resetStudent(s.id)}>Undo</button>}
            </div>
          </div>
        ))}
        {stationRoster.length === 0 && <p className="pci-empty">No students assigned to this station. Load a roster with a station column.</p>}
      </div>

      <div className="pci-log">
        <h2 className="pci-h2">Today's log — {stationLabel(stationId)}</h2>
        {stationLog.length === 0 ? (
          <p className="pci-empty">No scans yet at this station.</p>
        ) : (
          <table>
            <thead><tr><th>Time</th><th>Student</th><th>Action</th><th>Operator</th></tr></thead>
            <tbody>
              {stationLog.map((r, i) => (
                <tr key={i}>
                  <td>{r.time}</td>
                  <td>{r.name}</td>
                  <td style={{ color: r.early ? RED : NAVY }}>{r.action}{r.note ? ` · ${r.note}` : ""}</td>
                  <td>{r.operator || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}

      {adminTab === "reports" && (
      <div className="pci-reports">
        <div className="pci-report-controls">
          <label>
            Scope
            <select value={reportScope} onChange={(e) => setReportScope(e.target.value)}>
              <option value="all">All stations</option>
              {STATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label>
            History range
            <select value={reportRange} onChange={(e) => setReportRange(e.target.value)}>
              <option value="week">This week</option>
              <option value="all">All available days</option>
            </select>
          </label>
        </div>

        <section className="pci-report-block">
          <div className="pci-report-head">
            <h2 className="pci-h2">Current status — not checked in</h2>
            <div className="pci-report-actions">
              <button onClick={exportSnapshot}>Export CSV</button>
              <button onClick={() => window.print()}>Print</button>
            </div>
          </div>
          <p className="pci-report-sub">Real-time from live check-ins. Use this for the morning leadership snapshot. Scope: {reportScope === "all" ? "all stations" : stationLabel(reportScope)}.</p>
          <div className="pci-report-metrics">
            <span className="pci-metric" style={{ color: RED }}><b>{snapshot.notIn.length}</b> not checked in</span>
            <span className="pci-metric" style={{ color: NAVY }}><b>{snapshot.inCount}</b> phone in</span>
            <span className="pci-metric" style={{ color: "#2e7d32" }}><b>{snapshot.returnedCount}</b> returned</span>
            <span className="pci-metric" style={{ color: GOLD }}><b>{snapshot.noPhoneCount}</b> no phone</span>
            <span className="pci-metric"><b>{snapshot.total}</b> total</span>
          </div>
          {snapshot.notIn.length === 0 ? (
            <p className="pci-empty">Everyone in scope has checked in.</p>
          ) : (
            <table className="pci-report-table">
              <thead><tr><th>Student</th><th>ID</th><th>Station</th><th>Slot</th></tr></thead>
              <tbody>
                {snapshot.notIn.map((s) => (
                  <tr key={s.id}><td>{s.name}</td><td>{s.id}</td><td>{stationLabel(s.station)}</td><td>{s.slot}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="pci-report-block">
          <div className="pci-report-head">
            <h2 className="pci-h2">Chronic patterns</h2>
            <div className="pci-report-actions">
              <button onClick={exportChronic}>Export CSV</button>
            </div>
          </div>
          <p className="pci-report-sub">
            Over {chronic.dayCount} day{chronic.dayCount === 1 ? "" : "s"} ({chronic.first} to {chronic.last}).
            Missed check-ins are mornings with no phone turned in. Left behind are afternoons a phone was not retrieved.
          </p>
          <p className="pci-report-demo">Reads end-of-day records from Firestore over the selected range. Days with no records yet are skipped.</p>
          {chronic.list.length === 0 ? (
            <p className="pci-empty">No flagged students in this range and scope.</p>
          ) : (
            <table className="pci-report-table">
              <thead><tr><th>Student</th><th>Station</th><th>Missed check-ins</th><th>Left behind</th><th>Total</th></tr></thead>
              <tbody>
                {chronic.list.map((r) => (
                  <tr key={r.id} className={r.total >= 4 ? "flag" : ""}>
                    <td>{r.name}</td>
                    <td>{stationLabel(r.station)}</td>
                    <td>{r.missed}</td>
                    <td>{r.left}</td>
                    <td><b>{r.total}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      )}
    </div>
  );
}

const css = `
.pci-root{font-family:Calibri,'Segoe UI',system-ui,sans-serif;color:#1f2733;background:${BEIGE};padding:20px;max-width:1000px;margin:0 auto;}
.pci-root *{box-sizing:border-box;}
.pci-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;border-bottom:3px solid ${NAVY};padding-bottom:12px;flex-wrap:wrap;}
.pci-head h1{font-family:Georgia,serif;color:${NAVY};margin:0;font-size:26px;}
.pci-head p{margin:2px 0 0;color:${GOLD};font-size:13px;letter-spacing:.08em;text-transform:uppercase;}
.pci-station{display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;justify-content:flex-end;}
.pci-station label{font-size:12px;color:#5b6472;display:flex;flex-direction:column;gap:3px;}
.pci-station input,.pci-station select{font-size:14px;padding:6px 8px;border:1px solid #ccc;border-radius:6px;}
.pci-screen select{border:2px solid ${NAVY};color:${NAVY};font-weight:600;min-width:150px;}
.pci-date{font-size:13px;color:${NAVY};font-weight:600;align-self:center;}
.pci-note{font-size:12px;color:#5b6472;background:#fff;border:1px dashed ${GOLD};border-radius:6px;padding:8px 12px;margin:12px 0;}
.pci-note code{background:${BEIGE};padding:1px 5px;border-radius:4px;}
.pci-mode{display:flex;gap:8px;align-items:center;margin:16px 0;flex-wrap:wrap;}
.pci-mode button{font-size:16px;padding:10px 20px;border:2px solid ${NAVY};background:#fff;color:${NAVY};border-radius:8px;cursor:pointer;font-weight:600;}
.pci-mode button.on{background:${NAVY};color:#fff;}
.pci-early{display:flex;align-items:center;gap:6px;font-size:14px;color:${RED};font-weight:600;margin-left:4px;}
.pci-autonote{font-size:12px;color:#8a8f98;font-style:italic;margin-left:4px;}
.pci-scan{display:flex;gap:8px;}
.pci-scan input{flex:1;font-size:22px;padding:14px 16px;border:2px solid ${GOLD};border-radius:10px;}
.pci-scan input:focus{outline:none;border-color:${NAVY};box-shadow:0 0 0 3px rgba(35,60,95,.15);}
.pci-scan button{font-size:18px;padding:0 24px;background:${NAVY};color:#fff;border:none;border-radius:10px;cursor:pointer;}
.pci-diag{margin-top:10px;background:#fff;border:1px solid #d9d3c4;border-left:4px solid ${GOLD};border-radius:8px;padding:10px 14px;font-size:13px;color:#4a5262;}
.pci-diag strong{display:block;color:${NAVY};margin-bottom:4px;font-size:13px;}
.pci-diag code{background:${BEIGE};padding:1px 6px;border-radius:4px;font-size:13px;}
.pci-diag-warn{color:${RED};margin-top:3px;}
.pci-feedback{display:flex;align-items:center;gap:14px;border:2px solid;border-radius:10px;padding:14px 16px;margin-top:12px;font-size:15px;}
.pci-feedback strong{font-size:18px;display:block;}
.pci-slot{color:#fff;font-size:24px;font-weight:700;width:52px;height:52px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.pci-awaytag{font-size:12px;color:${GOLD};font-weight:600;margin-top:2px;}
.pci-summary{display:flex;gap:10px;flex-wrap:wrap;margin:16px 0;}
.pci-chip{padding:8px 14px;border-radius:20px;font-size:14px;font-weight:600;}
.pci-num{font-size:18px;font-weight:700;}
.pci-total{background:${NAVY};color:#fff;}
.pci-tools{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:12px;}
.pci-tools button{font-size:13px;padding:8px 12px;border:1px solid ${NAVY};background:#fff;color:${NAVY};border-radius:6px;cursor:pointer;}
.pci-reset{border-color:${RED}!important;color:${RED}!important;}
.pci-lookup{margin-bottom:20px;}
.pci-lookup>input{width:100%;font-size:14px;padding:10px 12px;border:1px solid #ccc;border-radius:6px;}
.pci-results{background:#fff;border:1px solid #e2ddd0;border-radius:6px;margin-top:6px;overflow:hidden;}
.pci-result{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;border-bottom:1px solid #f0ece1;}
.pci-result strong{color:${NAVY};margin-right:8px;}
.pci-result-meta{font-size:12px;color:#8a8f98;}
.pci-result-actions{display:flex;gap:6px;flex-shrink:0;}
.pci-result button{font-size:12px;padding:6px 12px;background:${NAVY};color:#fff;border:none;border-radius:5px;cursor:pointer;}
.pci-result button.ghost{background:#eef0f2;color:${NAVY};}
.pci-h2{font-family:Georgia,serif;color:${NAVY};font-size:18px;border-bottom:1px solid ${GOLD};padding-bottom:6px;margin-top:8px;}
.pci-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px;margin:12px 0 24px;}
.pci-card{background:#fff;border:1px solid #e2ddd0;border-left:5px solid;border-radius:8px;padding:12px;}
.pci-card-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;}
.pci-slotnum{font-size:12px;color:#5b6472;font-weight:600;}
.pci-badge{font-size:11px;padding:3px 8px;border-radius:12px;font-weight:600;}
.pci-name{font-size:16px;font-weight:600;color:${NAVY};}
.pci-meta{font-size:12px;color:#8a8f98;margin-bottom:8px;}
.pci-card-actions{display:flex;gap:6px;flex-wrap:wrap;}
.pci-card-actions button{font-size:12px;padding:6px 10px;border:none;background:${NAVY};color:#fff;border-radius:5px;cursor:pointer;}
.pci-card-actions button.ghost{background:#eef0f2;color:#5b6472;}
.pci-empty{color:#8a8f98;font-size:14px;}
.pci-log table{width:100%;border-collapse:collapse;font-size:14px;margin-top:8px;}
.pci-log th{text-align:left;color:#5b6472;font-size:12px;text-transform:uppercase;letter-spacing:.05em;padding:6px 8px;border-bottom:1px solid #ddd;}
.pci-log td{padding:6px 8px;border-bottom:1px solid #f0ece1;}
.pci-allout{border-color:#2e7d32!important;color:#2e7d32!important;font-weight:600;}
.pci-tabs{display:flex;gap:6px;margin:14px 0;}
.pci-tabs button{font-size:14px;padding:8px 18px;border:none;border-bottom:3px solid transparent;background:none;color:#5b6472;cursor:pointer;font-weight:600;}
.pci-tabs button.on{color:${NAVY};border-bottom-color:${NAVY};}
.pci-reports{margin-top:8px;}
.pci-report-controls{display:flex;gap:16px;flex-wrap:wrap;margin-bottom:16px;}
.pci-report-controls label{font-size:12px;color:#5b6472;display:flex;flex-direction:column;gap:3px;}
.pci-report-controls select{font-size:14px;padding:6px 10px;border:1px solid ${NAVY};border-radius:6px;color:${NAVY};min-width:160px;}
.pci-report-block{background:#fff;border:1px solid #e2ddd0;border-radius:10px;padding:16px 18px;margin-bottom:18px;}
.pci-report-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;}
.pci-report-head .pci-h2{margin:0;border:none;padding:0;}
.pci-report-actions{display:flex;gap:8px;}
.pci-report-actions button{font-size:13px;padding:7px 14px;border:1px solid ${NAVY};background:#fff;color:${NAVY};border-radius:6px;cursor:pointer;}
.pci-report-sub{font-size:13px;color:#5b6472;margin:8px 0;}
.pci-report-demo{font-size:12px;color:${GOLD};background:#faf6ea;border:1px dashed ${GOLD};border-radius:6px;padding:6px 10px;margin:6px 0 12px;}
.pci-report-metrics{display:flex;gap:18px;flex-wrap:wrap;margin:10px 0 14px;}
.pci-metric{font-size:14px;color:#5b6472;}
.pci-metric b{font-size:20px;}
.pci-report-table{width:100%;border-collapse:collapse;font-size:14px;}
.pci-report-table th{text-align:left;color:#5b6472;font-size:12px;text-transform:uppercase;letter-spacing:.05em;padding:7px 8px;border-bottom:2px solid #e2ddd0;}
.pci-report-table td{padding:7px 8px;border-bottom:1px solid #f0ece1;}
.pci-report-table tr.flag{background:#fdeceb;}
.pci-report-table tr.flag td{color:${RED};}
.pci-lock{position:fixed;inset:0;background:rgba(35,60,95,.92);display:flex;align-items:center;justify-content:center;z-index:50;}
.pci-lock-card{background:#fff;border-radius:14px;padding:32px 36px;max-width:420px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.35);}
.pci-lock-badge{display:inline-block;background:${RED};color:#fff;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:4px 12px;border-radius:20px;}
.pci-lock-card h2{font-family:Georgia,serif;color:${NAVY};margin:14px 0 6px;}
.pci-lock-card p{color:#5b6472;font-size:14px;margin:0 0 18px;}
.pci-lock-pin{display:flex;gap:8px;}
.pci-lock-pin input{flex:1;font-size:18px;padding:10px 14px;border:2px solid ${GOLD};border-radius:8px;text-align:center;letter-spacing:.3em;}
.pci-lock-pin button{font-size:16px;padding:0 22px;background:${NAVY};color:#fff;border:none;border-radius:8px;cursor:pointer;}
.pci-kiosk{font-family:Calibri,'Segoe UI',system-ui,sans-serif;background:${BEIGE};min-height:100vh;display:flex;flex-direction:column;}
.pci-kiosk-top{display:flex;justify-content:space-between;align-items:center;background:${NAVY};color:#fff;padding:14px 22px;font-family:Georgia,serif;font-size:20px;}
.pci-lock-btn{font-size:12px;padding:6px 14px;background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5);border-radius:6px;cursor:pointer;}
.pci-kiosk-body{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:24px;}
.pci-kiosk-scan{width:min(560px,88vw);}
.pci-kiosk-scan input{width:100%;font-size:22px;padding:14px 18px;text-align:center;border:2px solid ${GOLD};border-radius:12px;color:${NAVY};}
.pci-kiosk-scan input:focus{outline:none;border-color:${NAVY};box-shadow:0 0 0 4px rgba(35,60,95,.15);}
.pci-kiosk-card{background:#fff;border:4px solid;border-radius:20px;padding:48px 64px;text-align:center;min-width:min(560px,88vw);box-shadow:0 12px 40px rgba(0,0,0,.12);}
.pci-kiosk-name{font-family:Georgia,serif;font-size:44px;color:${NAVY};font-weight:700;line-height:1.1;}
.pci-kiosk-slot{display:inline-block;margin-top:14px;background:${NAVY};color:#fff;font-weight:800;font-size:34px;padding:8px 22px;border-radius:12px;letter-spacing:.02em;}
.pci-kiosk-msg{font-size:40px;font-weight:800;margin-top:24px;text-transform:uppercase;letter-spacing:.02em;}
.pci-kiosk-idle{text-align:center;color:${NAVY};}
.pci-kiosk-ready{font-family:Georgia,serif;font-size:52px;color:${GOLD};font-weight:700;}
.pci-kiosk-idle p{font-size:24px;color:#5b6472;margin-top:8px;}
@media(max-width:520px){.pci-head{flex-direction:column;}.pci-station{justify-content:flex-start;}.pci-kiosk-card{padding:32px 24px;}.pci-kiosk-name{font-size:32px;}.pci-kiosk-msg{font-size:30px;}}
`;